'use client';

import { faAngleDown, faAngleUp, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Modal, ModalContent, TextArea } from '@pickleballinc/react-ui';
import { useState } from 'react';

import { setShouldShowModal } from '@/components/ConnectDUPRModal/helpers';
import { useSendMessage } from '@/modules/league/new-components/rsvp-components/hooks';
import { Player } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { toast } from 'react-toastify';

interface SendMessageModal {
  openModal: boolean;
  title: string;
  setIsModalOpen: (show: boolean) => void;
  isAuthorized: boolean | null;
  selectedPlayer?: {
    fullName: string;
    userId: string;
  };
  sendMessageToMultiplePlayers?: boolean;
  isWaitlist?: boolean;
  playingPlayers?: Player[];
  waitListPlayers?: Player[];
  notPlayingPlayers?: Player[];
  leagueId: string;
  slug: string;
  sessionId: string;
  matchDateId: string;
  leagueTitle: string;
  gameDayTitle: string;
}

const SendMessageModal = ({
  openModal,
  title,
  setIsModalOpen,
  isAuthorized,
  selectedPlayer,
  sendMessageToMultiplePlayers,
  isWaitlist,
  playingPlayers,
  waitListPlayers,
  notPlayingPlayers,
  leagueId,
  slug,
  sessionId,
  matchDateId,
  leagueTitle,
  gameDayTitle,
}: SendMessageModal) => {
  const [messageContent, setMessageContent] = useState('');
  const [readMessageRestriction, setReadMessageRestriction] = useState(false);
  const [isPlayingChecked, setIsPlayingChecked] = useState(false);
  const [isWaitlistChecked, setIsWaitlistChecked] = useState(false);
  const [isNotPlayingChecked, setIsNotPlayingChecked] = useState(false);
  const [allPlayingPlayers, setAllPlayingPlayers] = useState<Player[]>([]);
  const [allWaitlistPlayers, setAllWaitlistPlayers] = useState<Player[]>([]);
  const [allNotPlayingPlayers, setAllNotPlayingPlayers] = useState<Player[]>([]);

  const { setLoading } = useLoader();
  const { mutate: sendMessage } = useSendMessage();

  let uuids: string[] = [];

  if (!openModal) return null;

  const closeModal = () => {
    setShouldShowModal();
    setIsModalOpen(false);
  };

  const filterPlayersBySendInGameTexts = (players: Player[]): Player[] => {
    return players.filter((player) => player.SendInGameTexts_1); // Only include players who have SendInGameTexts_1
  };

  const filteredPlayingPlayers = playingPlayers && filterPlayersBySendInGameTexts(playingPlayers);
  const filteredNotPlayingPlayers =
    notPlayingPlayers && filterPlayersBySendInGameTexts(notPlayingPlayers);
  const filteredWaitingPlayers = waitListPlayers && filterPlayersBySendInGameTexts(waitListPlayers);

  return (
    <Modal open={openModal}>
      <ModalContent className='flex w-[calc(100%-16px)] flex-col gap-4 !overflow-hidden !p-6 !outline-none md:max-w-[430px]'>
        <div className='relative'>
          <h2 className='text-center text-base font-semibold leading-[22px] text-gray-900'>
            {title}
          </h2>

          <div className='mt-4 w-full border-t-[0.5px] border-gray-200 px-2 md:block' />

          <div
            className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
            onClick={() => {
              closeModal();
            }}
          >
            <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
          </div>
        </div>
        {sendMessageToMultiplePlayers && (
          <div className='flex items-center justify-between md:justify-center md:gap-2 lg:gap-4'>
            <Checkbox
              size='md'
              onClick={() => {
                setIsPlayingChecked(!isPlayingChecked);
                if (!isPlayingChecked && filteredPlayingPlayers) {
                  setAllPlayingPlayers(filteredPlayingPlayers);
                }
              }}
              Text='Playing'
            />

            {isWaitlist ? (
              <Checkbox
                onClick={() => {
                  setIsWaitlistChecked(!isWaitlistChecked);
                  if (!isWaitlistChecked && filteredWaitingPlayers) {
                    setAllWaitlistPlayers(filteredWaitingPlayers);
                  }
                }}
                Text='Waitlist'
              />
            ) : null}

            <Checkbox
              onClick={() => {
                setIsNotPlayingChecked(!isNotPlayingChecked);
                if (!isNotPlayingChecked && filteredNotPlayingPlayers) {
                  setAllNotPlayingPlayers(filteredNotPlayingPlayers);
                }
              }}
              Text='Not playing'
            />
          </div>
        )}
        <TextArea
          placeholder='Your Message...'
          rows={8}
          cols={40}
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
        />

        <div
          className={`sticky top-0 flex cursor-pointer flex-row justify-between border-b bg-white ${
            readMessageRestriction ? 'border-b-leagues-700' : 'border-b-gray-300'
          } p-2 font-bold text-leagues-700 shadow-sm`}
          onClick={() => setReadMessageRestriction(!readMessageRestriction)}
        >
          <p className='flex text-xs md:text-sm'>Read text message restrictions</p>

          <FontAwesomeIcon
            icon={readMessageRestriction ? faAngleUp : faAngleDown}
            className='ml-2 text-leagues-700'
            size='lg'
          />
        </div>
        {readMessageRestriction && (
          <div className='flex flex-col gap-1 px-2 text-left text-xs md:text-sm'>
            <p>
              <span className='font-bold'>Absolutely no </span>content pertaining to CBD, Alcohol,
              Marijuana, or any substance what so ever.
            </p>
            <p>
              In addition, there is to be <span className='font-bold'>absolutely</span> no
              advertising used with this text service.
            </p>
            <p>
              <span className='font-bold'>ALL</span> messages are to be related to the league ONLY.
            </p>
            <p>
              <span className='font-bold'>ANY</span> violation will be an immediate termination of
              your league.
            </p>
          </div>
        )}
        <div className='flex items-center justify-between gap-4'>
          <Button
            variant='secondary'
            className='flex w-[200px] justify-center border !text-sm !text-gray-500 focus:!shadow-[unset]'
            onClick={() => {
              closeModal();
            }}
          >
            Close
          </Button>
          <Button
            variant='primary'
            className='flex w-[200px] justify-center !text-sm focus:!shadow-[unset]'
            disabled={!messageContent}
            onClick={() => {
              if (messageContent !== '' && isAuthorized) {
                setLoading(true);
                if (selectedPlayer) {
                  uuids.push(selectedPlayer.userId);
                  sendMessage(
                    {
                      messageText: messageContent,
                      userUuids: uuids,
                      leagueId,
                      slug,
                      sessionId,
                      matchDateId,
                      leagueTitle,
                      gameDayTitle,
                    },
                    {
                      onSuccess: (data) => {
                        if (data.message) {
                          toast.success(data.message);
                        }
                        setLoading(false);
                      },
                      onError: () => {
                        toast.error('Failed to send the message. Please try again.');
                        setLoading(false);
                      },
                    }
                  );
                } else if (allPlayingPlayers) {
                  if (allPlayingPlayers) {
                    allPlayingPlayers.forEach((player) => {
                      if (player.UserID_1) {
                        uuids.push(player.UserID_1);
                      }
                      if (player.UserID_2) {
                        uuids.push(player.UserID_2);
                      }
                    });
                  }

                  if (allWaitlistPlayers) {
                    allWaitlistPlayers.forEach((player) => {
                      if (player.UserID_1) {
                        uuids.push(player.UserID_1);
                      }
                      if (player.UserID_2) {
                        uuids.push(player.UserID_2);
                      }
                    });
                  }

                  if (allNotPlayingPlayers) {
                    allNotPlayingPlayers.forEach((player) => {
                      if (player.UserID_1) {
                        uuids.push(player.UserID_1);
                      }
                      if (player.UserID_2) {
                        uuids.push(player.UserID_2);
                      }
                    });
                  }

                  sendMessage(
                    {
                      messageText: messageContent,
                      userUuids: uuids,
                      leagueId,
                      slug,
                      sessionId,
                      matchDateId,
                      leagueTitle,
                      gameDayTitle,
                    },
                    {
                      onSuccess: (data) => {
                        if (data.message) {
                          toast.success(data.message);
                        }
                        setLoading(false);
                      },
                      onError: () => {
                        toast.error('Failed to send the message. Please try again.');
                        setLoading(false);
                      },
                    }
                  );
                  setAllNotPlayingPlayers([]);
                  setAllPlayingPlayers([]);
                  setAllWaitlistPlayers([]);
                  setIsNotPlayingChecked(false);
                  setIsPlayingChecked(false);
                  setIsWaitlistChecked(false);
                  uuids = [];
                }
                setLoading(false);
              }
              closeModal();
            }}
          >
            Send
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default SendMessageModal;
